import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";

export const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error(t('passwordsDontMatch'));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t('passwordMinLength'));
      return;
    }

    setLoading(true);

    const result = await register(formData.email, formData.password, formData.name);
    
    if (result.success) {
      toast.success(t('accountCreatedSuccess'));
      navigate("/account");
    } else {
      toast.error(result.error || "Registration failed");
    }
    
    setLoading(false);
  };

  return (
    <div className="pt-24 pb-16" data-testid="register-page">
      <div className="max-w-md mx-auto px-4 md:px-8">
        <div className="mb-10 text-center">
          <p className="label-style mb-2">{t('getStarted')}</p>
          <h1 className="section-title">{t('createAccount')}</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="name">{t('fullName')}</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="rounded-full mt-1"
              required
              data-testid="name-input"
            />
          </div>
          <div>
            <Label htmlFor="email">{t('email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="rounded-full mt-1"
              required
              data-testid="email-input"
            />
          </div>
          <div>
            <Label htmlFor="password">{t('password')}</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              className="rounded-full mt-1"
              required
              data-testid="password-input"
            />
          </div>
          <div>
            <Label htmlFor="confirmPassword">{t('confirmPassword')}</Label>
            <Input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              className="rounded-full mt-1"
              required
              data-testid="confirm-password-input"
            />
          </div>

          <Button
            type="submit"
            className="w-full rounded-full h-12 uppercase tracking-widest font-bold"
            disabled={loading}
            data-testid="register-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('creatingAccount')}
              </>
            ) : (
              t('createAccount')
            )}
          </Button>
        </form>

        <p className="text-center mt-6 text-muted-foreground">
          {t('alreadyHaveAccount')}{" "}
          <Link to="/login" className="text-accent hover:underline">
            {t('login')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
